import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import { PARTNER_TYPES, accentColorForType } from 'common/src/other/PartnerUtils';
import {
  Box, Typography, Stack, Paper, TextField, MenuItem, Button, Switch,
  FormControlLabel, Avatar, Grid, Chip
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';
import CircularLoading from '../components/CircularLoading';

const EMPTY = {
  partnerType: PARTNER_TYPES.HOTEL,
  businessName: '',
  legalName: '',
  taxId: '',
  phone: '',
  description: '',
  address: { line1: '', city: '', state: '', country: '', postalCode: '' },
  location: { lat: 0, lng: 0 },
  zoneId: '',
  currency: 'USD',
  amenities: { wifi: false, parking: false, pool: false, ac: false, breakfast: false },
  policies: { checkIn: '14:00', checkOut: '12:00', cancellation: 'flexible' },
  active: true,
  approved: false,
  images: { cover: null, gallery: {} },
};

export default function MyPartner() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((s) => s.auth);
  const partnerdata = useSelector((s) => s.partnerdata);
  const zonesdata = useSelector((s) => s.zonesdata);
  const [form, setForm] = useState(EMPTY);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [galleryFile, setGalleryFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { fetchPartnersByOwner, editPartner, fetchZones } = api;

  useEffect(() => {
    if (auth?.profile?.uid) {
      dispatch(fetchPartnersByOwner(auth.profile.uid));
      if (!zonesdata.zones) dispatch(fetchZones());
    }
  }, [dispatch, fetchPartnersByOwner, fetchZones, zonesdata.zones, auth?.profile?.uid]);

  useEffect(() => {
    const list = partnerdata.partners || [];
    if (list.length > 0) {
      setForm({ ...EMPTY, ...list[0], address: { ...EMPTY.address, ...(list[0].address || {}) }, location: { ...EMPTY.location, ...(list[0].location || {}) } });
      setCoverPreview(list[0].images?.cover || null);
    }
    setLoading(false);
  }, [partnerdata.partners]);

  const update = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const segs = path.split('.');
      let cur = next;
      for (let i = 0; i < segs.length - 1; i += 1) {
        cur[segs[i]] = { ...(cur[segs[i]] || {}) };
        cur = cur[segs[i]];
      }
      cur[segs[segs.length - 1]] = value;
      return next;
    });
  };

  const onCoverChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCoverFile(f);
    setCoverPreview(URL.createObjectURL(f));
  };

  const onGalleryAdd = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !form.id) return;
    const idx = Object.keys(form.images?.gallery || {}).length;
    setGalleryFile(f);
    await dispatch(editPartner({ id: form.id, imageIndex: idx, imageFile: f }, 'AddGalleryImage'));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      let id = form.id;
      const payload = {
        ...form,
        owner: form.owner || auth.profile.uid,
        ownerEmail: form.ownerEmail || auth.profile.email,
        active: form.active !== false,
      };
      if (!id) {
        id = await dispatch(editPartner(payload, 'Add'));
      } else {
        await dispatch(editPartner(payload, 'Update'));
      }
      if (coverFile && id) {
        await dispatch(editPartner({ id, coverFile }, 'UpdateCover'));
      }
      navigate('/partner/dashboard');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularLoading />;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT_FAMILY }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('partner_business') || 'Mi negocio'}
        </Typography>
        {form.id ? (
          <Chip
            label={form.approved ? (t('approved') || 'Aprobado') : (t('pending_approval') || 'Pendiente de aprobación')}
            color={form.approved ? 'success' : 'warning'}
          />
        ) : null}
      </Stack>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={1}>
              <Avatar src={coverPreview || undefined} variant="rounded" sx={{ width: 220, height: 160 }}>
                {(form.businessName || '?').slice(0, 1)}
              </Avatar>
              <Button component="label" startIcon={<PhotoCameraIcon />} variant="outlined" size="small">
                {t('upload_cover') || 'Subir portada'}
                <input hidden type="file" accept="image/*" onChange={onCoverChange} />
              </Button>
              {form.id && (
                <Button component="label" variant="text" size="small">
                  {t('add_gallery_image') || 'Añadir foto a la galería'}
                  <input hidden type="file" accept="image/*" onChange={onGalleryAdd} />
                </Button>
              )}
              {form.id && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center">
                  {Object.values(form.images?.gallery || {}).slice(0, 6).map((g, i) => (
                    <Avatar key={i} variant="rounded" src={g} sx={{ width: 50, height: 50 }} />
                  ))}
                </Stack>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label={t('partner_type') || 'Tipo'}
                  value={form.partnerType} onChange={(e) => update('partnerType', e.target.value)}>
                  {Object.values(PARTNER_TYPES).map((pt) => (
                    <MenuItem key={pt} value={pt}>{pt}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label={t('zone') || 'Zona'}
                  value={form.zoneId || ''} onChange={(e) => update('zoneId', e.target.value)}>
                  <MenuItem value="">—</MenuItem>
                  {(zonesdata.zones || []).map((z) => (
                    <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('business_name') || 'Nombre comercial'}
                  value={form.businessName} onChange={(e) => update('businessName', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('legal_name') || 'Razón social'}
                  value={form.legalName} onChange={(e) => update('legalName', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('tax_id') || 'RFC/RUT'}
                  value={form.taxId} onChange={(e) => update('taxId', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('phone') || 'Teléfono'}
                  value={form.phone} onChange={(e) => update('phone', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t('description') || 'Descripción'}
                  multiline rows={2} value={form.description}
                  onChange={(e) => update('description', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label={t('line1') || 'Calle y número'} value={form.address.line1}
                  onChange={(e) => update('address.line1', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label={t('city') || 'Ciudad'} value={form.address.city}
                  onChange={(e) => update('address.city', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label={t('country') || 'País'} value={form.address.country}
                  onChange={(e) => update('address.country', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" type="number" label="Lat" value={form.location.lat}
                  onChange={(e) => update('location.lat', parseFloat(e.target.value) || 0)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" type="number" label="Lng" value={form.location.lng}
                  onChange={(e) => update('location.lng', parseFloat(e.target.value) || 0)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label={t('currency') || 'Moneda'}
                  value={form.currency} onChange={(e) => update('currency', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={<Switch checked={form.active !== false} onChange={() => update('active', !form.active)} />}
                  label={t('active') || 'Activo'}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
          <Button onClick={() => navigate('/partner/dashboard')}>{t('cancel') || 'Cancelar'}</Button>
          <Button variant="contained" sx={{ backgroundColor: MAIN_COLOR }} onClick={onSave} disabled={saving}>
            {saving ? '...' : (t('save') || 'Guardar')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
